import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subscription, filter, Subject, takeUntil } from 'rxjs';
import { AppSettings } from 'src/app/config';
import { TokenService } from 'src/app/core/services/token.service';
import { MaterialModule } from 'src/app/material.module';
import { CoreService } from 'src/app/shared/services/core.service';
import { SideMenuService, PinnedNavItem } from '../../modules/sys/services/side-menu.service';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { HeaderComponent } from './vertical/header/header.component';
import { BrandingComponent } from './vertical/sidebar/branding.component';
import { NavItem } from './vertical/sidebar/nav-item/nav-item';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';

const MOBILE_VIEW = 'screen and (max-width: 768px)';
const TABLET_VIEW = 'screen and (min-width: 769px) and (max-width: 1024px)';
const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

interface apps { id: number; img: string; title: string; subtitle: string; link: string; }
interface quicklinks { id: number; title: string; link: string; }

@Component({
  selector: 'app-full',
  imports: [
    RouterModule, AppNavItemComponent, MaterialModule, CommonModule, SidebarComponent,
    NgScrollbarModule, TablerIconsModule, HeaderComponent, AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent, AppBreadcrumbComponent, CustomizerComponent, BrandingComponent
  ],
  templateUrl: './full.component.html',
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy {
  navItems: NavItem[] = [];
  originalNavItems: NavItem[] = [];

  // Add destroy subject for proper cleanup
  private destroy$ = new Subject<void>();
  private pristineOriginalStructure: NavItem[] = [];
  private originalMenuStructure: Map<number, { parentId: number, originalIndex: number, siblings: any[] }> = new Map();

  @ViewChild('leftsidenav') public sidenav!: MatSidenav;
  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  resView = false;

  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;


  // for mobile app sidebar
  apps: apps[] = [
    {
      id: 1,
      img: '/assets/images/svgs/icon-dd-chat.svg',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/apps/chat',
    },
    {
      id: 2,
      img: '/assets/images/svgs/icon-dd-cart.svg',
      title: 'eCommerce App',
      subtitle: 'Buy a Product',
      link: '/apps/email/inbox',
    },
    {
      id: 3,
      img: '/assets/images/svgs/icon-dd-invoice.svg',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/apps/invoice',
    },
    {
      id: 4,
      img: '/assets/images/svgs/icon-dd-date.svg',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/apps/calendar',
    },
    {
      id: 5,
      img: '/assets/images/svgs/icon-dd-mobile.svg',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/apps/contacts',
    },
    {
      id: 6,
      img: '/assets/images/svgs/icon-dd-lifebuoy.svg',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/apps/tickets',
    },
    {
      id: 7,
      img: '/assets/images/svgs/icon-dd-message-box.svg',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/apps/email/inbox',
    },
    {
      id: 8,
      img: '/assets/images/svgs/icon-dd-application.svg',
      title: 'Courses',
      subtitle: 'Create new course',
      link: '/apps/courses',
    },
  ];

  quicklinks: quicklinks[] = [
    {
      id: 1,
      title: 'Pricing Page',
      link: '/theme-pages/pricing',
    },
    {
      id: 2,
      title: 'Authentication Design',
      link: '/authentication/login',
    },
    {
      id: 3,
      title: 'Register Now',
      link: '/authentication/side-register',
    },
    {
      id: 4,
      title: '404 Error Page',
      link: '/authentication/error',
    },
    {
      id: 5,
      title: 'Notes App',
      link: '/apps/notes',
    },
    {
      id: 6,
      title: 'Employee App',
      link: '/apps/employee',
    },
    {
      id: 7,
      title: 'Todo Application',
      link: '/apps/todo',
    },
    {
      id: 8,
      title: 'Treeview',
      link: '/theme-pages/treeview',
    },
  ];
  get isOver(): boolean { return this.isMobileScreen; }
  get isTablet(): boolean { return this.resView; }

  constructor(
    private settings: CoreService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private menuService: SideMenuService,
    private token: TokenService,
  ) {
    this.htmlElement = document.querySelector('html')!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe(state => {
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        if (!this.options.sidenavCollapsed) this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];
      });

    this.receiveOptions(this.options);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)  // Add takeUntil for proper cleanup
      )
      .subscribe(() => this.content.scrollTo({ top: 0 }));
  }

  ngOnInit() {
    const userId = this.token.getUserIdFromToken();

    // Get cached menu first
    this.menuService.getMenu()
      .pipe(takeUntil(this.destroy$))
      .subscribe(cachedMenu => {
        if (cachedMenu && cachedMenu.length > 0) {
          // Store pristine copy ONLY if we don't have one yet
          if (this.pristineOriginalStructure.length === 0) {
            this.pristineOriginalStructure = JSON.parse(JSON.stringify(cachedMenu));
            this.storeOriginalMenuStructure(this.pristineOriginalStructure);
          }
          this.originalNavItems = [...cachedMenu];
          this.organizeMenuItems();
        }
      });

    // Fetch fresh menu
    this.menuService.fetchMenu(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(freshMenu => {
        if (freshMenu && freshMenu.length > 0) {
          // Store pristine copy ONLY on first fetch (or if we don't have one)
          if (this.pristineOriginalStructure.length === 0) {
            this.pristineOriginalStructure = JSON.parse(JSON.stringify(freshMenu));
            this.storeOriginalMenuStructure(this.pristineOriginalStructure);
          }
          this.originalNavItems = [...freshMenu];
          this.organizeMenuItems();
        }
      });

    // Subscribe to pinned items updates
    this.menuService.pinnedItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.organizeMenuItems());
  }






  // FIXED: Proper order incrementing and child movement to top

  organizeMenuItems(): void {
    if (!this.pristineOriginalStructure.length) {
      console.warn('No pristine original structure available');
      return;
    }


    // 
    const menuItems = JSON.parse(JSON.stringify(this.pristineOriginalStructure));

    // 
    const activePinnedItems = this.menuService.getCurrentPinnedItems()
      .filter(item => item.isActive === true);


    //  map  pinned items
    const pinnedMap = new Map();
    activePinnedItems.forEach(pinned => {
      pinnedMap.set(pinned.sideMenuId, pinned);
    });

    //  make pinned
    const applyCurrentStatus = (items: any[]) => {
      items.forEach(item => {
        const pinnedInfo = pinnedMap.get(item.id);

        if (pinnedInfo) {
          item.isPinned = true;
          item.pinnedOrder = pinnedInfo.orderNo;
          item.userSideMenuIsActive = true;
          item.displayOrder = -Math.abs(item.displayOrder || 0);

        } else {
          item.isPinned = false;
          item.pinnedOrder = null;
          item.userSideMenuIsActive = false;
          item.displayOrder = Math.abs(item.displayOrder || 0);
        }

        if (item.children && item.children.length > 0) {
          applyCurrentStatus(item.children);
        }
      });
    };

    applyCurrentStatus(menuItems);

    // ✨ استخرج فقط الـ children المثبتين (تجاهل الـ parents المثبتين)
    const extractPinnedChildren = (items: any[], parent: any = null, collector: any[] = []) => {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // إذا هو child + pinned → انقله
        if (parent && item.isPinned) {
          parent.children = parent.children.filter((c: any) => c.id !== item.id);
          collector.push(item);
        }

        if (item.children && item.children.length > 0) {
          extractPinnedChildren(item.children, item, collector);
        }
      }
      return collector;
    };


    const pinnedChildren = extractPinnedChildren(menuItems);
    menuItems.unshift(...pinnedChildren);

    // ✨ رتّب القائمة (pinned أولاً حسب orderNo، ثم العاديين حسب displayOrder)
    const sortAtEachLevel = (items: any[]) => {
      items.sort((a, b) => {
        if (a.isPinned && b.isPinned) {
          return (a.pinnedOrder || 0) - (b.pinnedOrder || 0);
        }
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;

        const aOrder = Math.abs(a.displayOrder || 0);
        const bOrder = Math.abs(b.displayOrder || 0);
        return aOrder - bOrder;
      });

      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortAtEachLevel(item.children);
        }
      });
    };

    sortAtEachLevel(menuItems);
    this.navItems = menuItems;

  }



  


  private storeOriginalMenuStructure(menuItems: any[]): void {
    this.originalMenuStructure.clear();

    const storeRecursive = (items: any[], parentId: number = 0) => {
      items.forEach((item, index) => {
        this.originalMenuStructure.set(item.id, {
          parentId: parentId,
          originalIndex: index,
          siblings: items.map(sibling => ({
            id: sibling.id,
            displayOrder: sibling.displayOrder,
            nameEn: sibling.nameEn
          }))
        });


        if (item.children && item.children.length > 0) {
          storeRecursive(item.children, item.id);
        }
      });
    };

    storeRecursive(menuItems);
  }


  pinItem(item: any, note: string = '') {

    // جيب القائمة الحالية من service (بترتيبها الصحيح)
    const currentPinned = this.menuService.getCurrentPinnedItems();

    // احسب newOrder حسب طول القائمة
    const newOrder = currentPinned.length + 1;

    this.menuService.pinItemById(item.id, newOrder).subscribe({
      next: (responses) => {
        // organizeMenuItems رح يعيد الترتيب تلقائي
      },
      error: (error) => {
        console.error('Pin operation failed:', error);
      }
    });
  }



  unpinItem(item: any) {
    const pinnedItem = this.menuService.getPinnedItem(item.id);
    if (pinnedItem && pinnedItem.id) {
      this.menuService.unpinItemById(pinnedItem.id).subscribe({
        next: (responses) => {
          // The organizeMenuItems will be called automatically via pinnedItems$ subscription
        },
        error: (error) => {
          console.error('Unpin operation failed:', error);
        }
      });
    }
  }

  ngOnDestroy() {
    // Proper cleanup
    this.layoutChangesSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings) {
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
  }

  toggleColorsTheme(options: AppSettings) {
    this.htmlElement.classList.forEach(cls => {
      if (cls.endsWith('_theme')) {
        this.htmlElement.classList.remove(cls);
      }
    });
    this.htmlElement.classList.add(options.activeTheme);
  }
}